import React, { MutableRefObject, useCallback, useState, useMemo } from 'react';
import { Box, Button, Divider, Flex, Icon, Text, HStack } from '@televet/kibble-ui';
import { Buttons, Forms, NextStepSelect, QuestionMessage, StatusChangeSelect } from './PromptFields';
import { InvoiceAmount } from './PromptFields/InvoiceAmount';
import { AutomationRunActionPromptType } from 'shared/types/graphql';
import { getPromptOptionDetailByType } from 'pages/Automations/types/PromptOptionDetail';
import { AutomationPrompt } from '../../../types/AutomationPrompt';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  removeAutomationPrompt,
  updateAutomationErrors,
  updateAutomationPromptOrder,
} from 'pages/Automations/state/automationsSlice';
import { AutomationsPromptBlockFieldContainer } from '../../Layout';
import { PromptErrorDetail } from 'pages/Automations/types/AutomationValidation';
import partial from 'lodash/partial';
import { AddPromptButton } from './AddPromptButton';
import { SelectPromptTypeBlock } from './SelectPromptTypeBlock';

enum PromptOrderDirection {
  Up = 'Up',
  Down = 'Down',
}

interface PromptBlockProps {
  index: number;
  prompt: AutomationPrompt;
  errors?: PromptErrorDetail;
  isFirstPrompt: boolean;
  isLastPrompt: boolean;
  promptListRefs: MutableRefObject<HTMLDivElement[] | undefined>;
  promptBlockCallbackRef: (index: number) => void;
  setScrollToPromptIndex: React.Dispatch<React.SetStateAction<{ currentIndex: number; newIndex: number }>>;
}

export const PromptBlock = ({
  index,
  prompt,
  errors,
  isFirstPrompt,
  isLastPrompt,
  promptListRefs,
  promptBlockCallbackRef,
  setScrollToPromptIndex,
}: PromptBlockProps): JSX.Element | null => {
  const { promptType, channelStatusChangeId } = prompt;
  const dispatch = useAppDispatch();
  const promptErrors = useAppSelector((state) => state.automations.errors.prompts);
  const buttonErrors = useMemo(
    () => ({ containerErrors: errors?.buttonContainer, buttonErrors: errors?.buttons }),
    [errors],
  );

  const [selectPromptTypeBlockIsVisible, setSelectPromptTypeBlockIsVisible] = useState(false);

  const handleDeletePromptBlock = useCallback(() => {
    dispatch(removeAutomationPrompt({ promptIndex: index }));

    if (promptErrors) {
      const promptsList = promptErrors.filter((error, errorIndex) => errorIndex !== index);
      dispatch(
        updateAutomationErrors({
          prompts: promptsList.map((prompt, index) => {
            return { ...prompt, promptIndex: index };
          }),
        }),
      );
    }
  }, [index, dispatch, promptErrors]);

  const handleUpdatePromptOrder = useCallback(
    (direction: PromptOrderDirection) => {
      const newIndex = direction === PromptOrderDirection.Up ? index - 1 : index + 1;
      setScrollToPromptIndex({ currentIndex: index, newIndex });
      dispatch(updateAutomationPromptOrder({ currentIndex: index, newIndex }));
    },
    [dispatch, setScrollToPromptIndex, index],
  );

  const promptOptionDetail = getPromptOptionDetailByType(prompt?.promptType);

  if (!prompt) {
    return null;
  }

  return (
    <Box
      w="100%"
      ref={(el: HTMLDivElement): void => {
        promptListRefs.current ? (promptListRefs.current[index] = el) : null;
        promptBlockCallbackRef(index);
      }}
      scrollMargin="150px"
    >
      <Box
        border="1px"
        borderColor="border.default"
        className="PromptBlock"
        bg="background.subtle"
        borderRadius="md"
        pb={4}
        w="100%"
      >
        <Flex className="PromptBlock__Header" py={2} pl={4} pr={2} justify="space-between" align="center">
          <Flex align="center">
            <Icon name={promptOptionDetail.iconName} size="xl" />
            <Text size="lg" ml={2} fontWeight="bold">
              {index + 1}. {promptOptionDetail.title}
            </Text>
          </Flex>
          <HStack>
            <Button
              variant="ghostNeutral"
              iconName="arrowDown"
              isDisabled={isLastPrompt}
              onClick={partial(handleUpdatePromptOrder, PromptOrderDirection.Down)}
            />
            <Button
              variant="ghostNeutral"
              iconName="arrowUp"
              disabled={isFirstPrompt}
              onClick={partial(handleUpdatePromptOrder, PromptOrderDirection.Up)}
            />

            <Button variant="ghostNeutral" iconName="trashcan" onClick={handleDeletePromptBlock} />
          </HStack>
        </Flex>
        <Divider />
        <AutomationsPromptBlockFieldContainer>
          <StatusChangeSelect
            promptType={prompt.promptType}
            promptIndex={index}
            channelStatusChangeId={channelStatusChangeId}
            isInitialStatusChange
          />
          <Divider pt={6} />
        </AutomationsPromptBlockFieldContainer>
        <QuestionMessage
          promptType={prompt.promptType}
          config={prompt.config}
          promptIndex={index}
          errors={errors?.questionOrMessage}
        />
        {promptType === AutomationRunActionPromptType.Buttons && (
          <Buttons promptType={prompt.promptType} config={prompt.config} promptIndex={index} errors={buttonErrors} />
        )}
        {promptType === AutomationRunActionPromptType.Form && (
          <Forms prompt={prompt} promptIndex={index} errors={errors?.form} />
        )}
        {promptType === AutomationRunActionPromptType.Payment && (
          <InvoiceAmount
            triggerActionNumber={prompt.triggerActionNumber}
            config={prompt.config}
            promptType={prompt.promptType}
            promptIndex={index}
            errors={{ invoiceErrors: errors?.invoice, nextStepErrors: errors?.nextStep }}
          />
        )}
        {promptType !== AutomationRunActionPromptType.Buttons && promptType !== AutomationRunActionPromptType.Payment && (
          <AutomationsPromptBlockFieldContainer>
            <Divider mb={6} />
            <NextStepSelect promptIndex={index} />
          </AutomationsPromptBlockFieldContainer>
        )}
      </Box>
      <AddPromptButton
        isDisabled={selectPromptTypeBlockIsVisible}
        onClick={(): void => setSelectPromptTypeBlockIsVisible((prevState) => !prevState)}
      />
      {selectPromptTypeBlockIsVisible && (
        <SelectPromptTypeBlock
          closeButtonVisible
          toggleVisiblity={(): void => setSelectPromptTypeBlockIsVisible((prevState) => !prevState)}
          addPromptAtIndex={index + 1}
          selectPromptTypeBlockCallbackRef={(el): void => {
            if (el) {
              // Scroll the select prompt type block into view when made visible
              el.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        />
      )}
    </Box>
  );
};
